import React, { useCallback, useState } from 'react';
import {
  CircularProgress,
  Modal,
  Autocomplete,
  TextField
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Select from 'react-select';
import { ErrorMessage, InputItem } from '../../Form';
import { actions } from '../../../../redux/store/store';
import { autocomplete, errorAutoComplete } from '../../orderHelper/styles';
import {
  useCreateMetroMutation,
  useGetMetrosQuery,
  useUpdateMetroMutation
} from '../../../../api/metro';
import { checkForError } from '../../../../constants/extras/errorHandlers';
import { metroOptionsStyles } from './metroSelect';
import { useUpdateDriverMutation } from '../../../../api/drivers';
import { sendSuccessMessage } from '../../../../constants/extras/sendSuccessMsg';
import './style.scss';

const data = ['Percentage (%)', 'Dollar ($)'];

const requiredMessage = 'This field is required';
const validationSchema = Yup.object().shape(
  {
    name: Yup.string()
      .test('len', 'Please Enter valid metro name', (value) => {
        return !!value.trim();
      })
      .required(requiredMessage),

    bonus: Yup.string()
      .ensure()
      .when('bonusType', {
        is: (bonusType) => bonusType !== '',
        then: Yup.string().required('Bonus is required field')
      }),
    bonusType: Yup.string()
      .ensure()
      .when('bonus', {
        is: (bonus) => bonus,
        then: Yup.string().required('Bonus Type is required field')
      }),
    isBackgroundCheck: Yup.boolean()
      .default(true)
      .required('Background Check is required field'),
    isAdminActive: Yup.boolean()
      .default(true)
      .required('Admin Side is required field'),
    isDriverActive: Yup.boolean()
      .default(true)
      .required('Driver Side is required field'),
    isWaitingActive: Yup.boolean()
      .default(false)
      .required('Waiting List is required field'),
    isDriverIntakeCheck: Yup.boolean()
      .default(false)
      .required('Driver Intake is required field'),
    isBranchActive: Yup.boolean()
      .default(false)
      .required('Branch Active is required field'),
    isOAIActive: Yup.boolean()
      .default(false)
      .required('OAI Active is required field'),
    approvalMode: Yup.string()
      .when('isBackgroundCheck', {
        is: true,
        then: Yup.string()
          .oneOf(['fullProcess', 'mvrFirst'])
          .required('Approval Mode is required field'),
        otherwise: Yup.string()
          .oneOf(['fullProcess', 'mvrFirst'])
          .nullable(),
      })
      .default('fullProcess')
  },
  [['bonus', 'bonusType']]
);

const MetroModal = (props) => {
  const { id } = props;
  const isReadOnly = Boolean(id?._id && id?.readOnly);
  const checkForUpdateMetro =
    id && id?.checkForUpdateMetro && id?.checkForUpdateMetro;
  const metro = props && props?.id?.metro && props?.id?.metro;
  const driverId = props && props?.id?.id && props?.id?.id;
  const checkForUpdateDriversMetro =
    checkForUpdateMetro === 'CHANGE__DRIVERS__METRO';
  const title =
    id && id._id
      ? `Metro ${id && id?._id ? '#'.concat(id?._id) : ''}`
      : !checkForUpdateDriversMetro
        ? 'New Metro'
        : 'Update Metro';

  const { data: metros, isFetching } = useGetMetrosQuery(null, {
    refetchOnMountOrArgChange: true,
    skip: !checkForUpdateDriversMetro || !driverId
  });

  const [updateDriverMetro, { isLoading: isUpdatingDriver }] =
    useUpdateDriverMutation();

  const [create, { error: createError, isLoading: isCreating }] =
    useCreateMetroMutation();

  const [update, { error: updateError, isLoading: isUpdating }] =
    useUpdateMetroMutation();
  const [selectedOption, setSelectedOption] = useState({
    value: metro || '',
    label: metro || ''
  });
  const [options, setOptions] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDriverSideCheck, setIsDriverSideCheck] = useState(
    id ? (id?.isDriverActive === false ? false : true) : true
  );

  const form = useForm({
    defaultValues: checkForUpdateDriversMetro
      ? {
        metro: metro || '',
        bonus: id && id?.bonus ? id?.bonus : '',
        bonusType:
          id && id?.bonusType === 'percentage'
            ? 'Percentage (%)'
            : id && id?.bonusType === 'dollar'
              ? 'Dollar ($)'
              : '',
        isBackgroundCheck:
          id && id?.isBackgroundCheck === false ? false : true,
        isAdminActive: id && id?.isAdminActive === false ? false : true,
        isDriverActive: id && id?.isDriverActive === false ? false : true,
        isWaitingActive: id && id?.isWaitingActive === false ? false : true,
        isDriverIntakeCheck: id && id?.isDriverIntakeCheck === false ? false : true,
        isBranchActive: id && id?.isBranchActive === true ? true : false,
        isOAIActive: id && id?.isOAIActive === true ? true : false,
        approvalMode: id && id?.approvalMode ? id?.approvalMode : 'fullProcess'
      }
      : {
        name: id && id?.name ? id?.name : '',
        code: id && id?.code ? id?.code : '',
        bonus: id && id?.bonus ? id?.bonus : '',
        bonusType:
          id && id?.bonusType === 'percentage'
            ? 'Percentage (%)'
            : id && id?.bonusType === 'dollar'
              ? 'Dollar ($)'
              : '',
        isBackgroundCheck:
          id && id?.isBackgroundCheck === false ? false : true,
        isAdminActive: id && id?.isAdminActive === false ? false : true,
        isDriverActive: id && id?.isDriverActive === false ? false : true,
        isWaitingActive: id && id?.isWaitingActive === false ? false : true,
        isDriverIntakeCheck: id && id?.isDriverIntakeCheck === false ? false : true,
        isBranchActive: id && id?.isBranchActive === true ? true : false,
        isOAIActive: id && id?.isOAIActive === true ? true : false,
        approvalMode: id && id?.approvalMode ? id?.approvalMode : 'fullProcess'
      },
    resolver: yupResolver(validationSchema)
  });
  const { control } = form;

  const onSubmit = useCallback(
    (data) => {
      if (checkForUpdateDriversMetro) {
        (async () => {
          let result = await updateDriverMetro({
            metro: selectedOption?.value,
            _id: driverId
          });
          console.log('result', result);
          if (!result?.error && result?.data?.metro === selectedOption?.value) {
            sendSuccessMessage('Metro updated successfully');
            actions.refetch.setIsDataRefetch(true);
          } else {
            checkForError(result);
          }
          actions.modal.closeMetro();
        })();
      } else {
        (async () => {
          // Check and modify data.bonusType based on conditions
          if (data.bonusType === 'Percentage (%)') {
            data.bonusType = 'percentage';
          } else if (data.bonusType === 'Dollar ($)') {
            data.bonusType = 'dollar';
          }

          (data.bonusType === '' || data.bonus === '0') &&
            (data.bonusType = null);
          data.bonus === '0' && (data.bonus = '');

          let result = await (id && id?._id
            ? update({
              name: data?.name,
              code: data?.code,
              bonus: data?.bonus,
              bonusType: data?.bonusType,
              isBackgroundCheck: data?.isBackgroundCheck,
              isAdminActive: data?.isAdminActive,
              isDriverActive: data?.isDriverActive,
              isWaitingActive: data?.isWaitingActive,
              isDriverIntakeCheck: data?.isDriverIntakeCheck,
              isBranchActive: data?.isBranchActive,
              isOAIActive: data?.isOAIActive,
              approvalMode: data?.approvalMode,
              _id: id && id?._id
            })
            : create(data));
          if (!result?.data?.error && result?.data?.name) {
            sendSuccessMessage(
              `Metro ${id?._id ? 'updated' : 'created'} successfully`
            );
          } else {
            const error = updateError || createError;
            checkForError(error || result);
          }
          actions.modal.closeMetro();
        })();
      }
    },
    [
      id,
      driverId,
      selectedOption.value,
      checkForUpdateDriversMetro,
      updateDriverMetro,
      create,
      update,
      createError,
      updateError
    ]
  );

  const onCancel = useCallback(() => {
    if (
      !form.formState.isDirty ||
      window.confirm('Do you want to close the dialog? Changes will be lost.')
    ) {
      actions.modal.closeMetro();
    }
  }, [form.formState.isDirty]);

  React.useEffect(() => {
    setOptions(
      !isFetching &&
      metros?.length > 0 &&
      metros?.map(function (item) {
        return {
          value: item?.name,
          label: item?.name
        };
      })
    );
  }, [metros, isFetching]);

  return (
    <Modal
      open
      onClose={onCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className={`modal after-loading`} style={{ maxWidth: '34.5rem' }}>
        <div className="modal__header">
          <h2 className="modal__title">{title}</h2>
        </div>
        <div className="modal__body">
          {!checkForUpdateDriversMetro && (
            <form>
              <fieldset disabled={isReadOnly} style={{ border: 0, padding: 0, margin: 0 }}>
              <InputItem
                name="name"
                form={form}
                title="Metro"
                autoComplete="off"
              />
              <InputItem
                name="code"
                form={form}
                title="Code"
                autoComplete="off"
              />
              <InputItem
                name="bonus"
                form={form}
                title="Bonus"
                autoComplete="off"
                numeric
              />
              <div className="w-auto d-flex flex-column">
                <label style={{ marginBottom: '0.1rem' }}>Bonus Type</label>
                <Controller
                  control={control}
                  name="bonusType"
                  render={({
                    field: { onChange, value, ref },
                    fieldState: { error }
                  }) => {
                    const styleObj = error
                      ? { ...errorAutoComplete }
                      : { ...autocomplete };
                    return (
                      <React.Fragment>
                        <Autocomplete
                          noOptionsText={'Select Bonus Type'}
                          options={data}
                          value={value || ''}
                          getOptionLabel={(option) => option}
                          sx={{
                            ...styleObj
                          }}
                          onChange={(e, data) => {
                            onChange(data);
                          }}
                          blurOnSelect={true}
                          ref={ref}
                          isAll={true}
                          isError={error && true}
                          {...props}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Bonus Type"
                              inputProps={{
                                ...params.inputProps
                              }}
                              required={false}
                            />
                          )}
                        />
                        <ErrorMessage error={error} />
                      </React.Fragment>
                    );
                  }}
                />
              </div>

              <div
                className="d-flex"
                style={{
                  gap: '30px',
                  justifyContent: 'space-between',
                  padding: '0 8px'
                }}
              >
                <div className=" d-flex flex-row mt-2 align-items-center gap-1">
                  <label style={{ marginBottom: '0.1rem' }}>
                    Driver Side :
                  </label>
                  <Controller
                    control={control}
                    name="isDriverActive"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error }
                    }) => {
                      return (
                        <React.Fragment>
                          <div className="toggle_container_metros pl-2">
                            <div className="toggle-switch-metros">
                              <input
                                type="checkbox"
                                className="checkbox"
                                name="isDriverActive"
                                id="driver"
                                checked={value}
                                onChange={(e) => {
                                  onChange(e.target.checked);
                                  setIsDriverSideCheck(e.target.checked);
                                }}
                              />
                              <label className="label" htmlFor="driver">
                                <span className="backgroundC" />
                                <span className="switch-metros" />
                              </label>
                            </div>
                          </div>
                          <ErrorMessage error={error} />
                        </React.Fragment>
                      );
                    }}
                  />
                </div>
                <div className=" d-flex flex-row mt-2 align-items-center gap-1">
                  <label style={{ marginBottom: '0.1rem' }}>Admin Side :</label>
                  <Controller
                    control={control}
                    name="isAdminActive"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error }
                    }) => {
                      return (
                        <React.Fragment>
                          <div className="toggle_container_metros pl-2">
                            <div className="toggle-switch-metros">
                              <input
                                type="checkbox"
                                className="checkbox"
                                name="isAdminActive"
                                id="admin"
                                checked={value}
                                onChange={(e) => {
                                  onChange(e.target.checked);
                                  if (isDriverSideCheck) {
                                    form.setValue(
                                      'isDriverActive',
                                      e.target.checked
                                    );
                                  }
                                }}
                              />
                              <label className="label" htmlFor="admin">
                                <span className="backgroundC" />
                                <span className="switch-metros" />
                              </label>
                            </div>
                          </div>
                          <ErrorMessage error={error} />
                        </React.Fragment>
                      );
                    }}
                  />
                </div>
              </div>

              <div
                className="d-flex"
                style={{
                  gap: '30px',
                  justifyContent: 'space-between',
                  padding: '0 8px'
                }}
              >
                <div className="d-flex flex-row mt-2 align-items-center gap-1">
                  <label style={{ marginBottom: '0.1rem' }}>
                    Background Check :
                  </label>
                  <Controller
                    control={control}
                    name="isBackgroundCheck"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error }
                    }) => {
                      return (
                        <React.Fragment>
                          <div className="toggle_container_metros pl-2">
                            <div className="toggle-switch-metros">
                              <input
                                type="checkbox"
                                className="checkbox"
                                name="isBackgroundCheck"
                                id="on"
                                checked={value}
                                onChange={(e) => {
                                  onChange(e.target.checked);
                                }}
                              />
                              <label className="label" htmlFor="on">
                                <span className="backgroundC" />
                                <span className="switch-metros" />
                              </label>
                            </div>
                          </div>
                          <ErrorMessage error={error} />
                        </React.Fragment>
                      );
                    }}
                  />
                </div>

                <div className="d-flex flex-row mt-2 align-items-center gap-1">
                  <label style={{ marginBottom: '0.1rem' }}>
                    Waiting List :
                  </label>
                  <Controller
                    control={control}
                    name="isWaitingActive"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error }
                    }) => {
                      return (
                        <React.Fragment>
                          <div className="toggle_container_metros pl-2">
                            <div className="toggle-switch-metros">
                              <input
                                type="checkbox"
                                className="checkbox"
                                name="isWaitingActive"
                                id="waiting"
                                checked={value}
                                onChange={(e) => {
                                  onChange(e.target.checked);
                                }}
                              />
                              <label className="label" htmlFor="waiting">
                                <span className="backgroundC" />
                                <span className="switch-metros" />
                              </label>
                            </div>
                          </div>
                          <ErrorMessage error={error} />
                        </React.Fragment>
                      );
                    }}
                  />
                </div>

              </div>
              <div className="d-flex" style={{ gap: '30px', justifyContent: 'space-between', padding: '0 8px' }}>
                <div className="d-flex flex-row mt-2 align-items-center gap-1">
                  <label style={{ marginBottom: '0.1rem' }}>Branch Active :</label>
                  <Controller
                    control={control}
                    name="isBranchActive"
                    render={({ field: { onChange, value } }) => (
                      <div className="toggle-switch-metros">
                        <input
                          type="checkbox"
                          className="checkbox"
                          name="isBranchActive"
                          id="branch-active"
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                        <label className="label" htmlFor="branch-active">
                          <span className="backgroundC" />
                          <span className="switch-metros" />
                        </label>
                      </div>
                    )}
                  />
                </div>
                <div className="d-flex flex-row mt-2 align-items-center gap-1">
                  <label style={{ marginBottom: '0.1rem' }}>OAI Active :</label>
                  <Controller
                    control={control}
                    name="isOAIActive"
                    render={({ field: { onChange, value } }) => (
                      <div className="toggle-switch-metros">
                        <input
                          type="checkbox"
                          className="checkbox"
                          name="isOAIActive"
                          id="oai-active"
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                        <label className="label" htmlFor="oai-active">
                          <span className="backgroundC" />
                          <span className="switch-metros" />
                        </label>
                      </div>
                    )}
                  />
                </div>
              </div>

              {
                form.watch('isBackgroundCheck') ?
                <div
                  className="mt-3 p-4"
                  style={{
                    backgroundColor: '#f5f6f8',
                    borderRadius: '18px',
                    width: '100%',
                    border: 'none',
                  }}
                  >
                  {/* Title */}
                  <div
                    className="fw-bold mb-3"
                    style={{
                      fontSize: '16px',
                      lineHeight: '1.2',
                      color: '#171717',
                    }}
                  >
                    Approval Mode
                  </div>

                  <Controller
                    control={control}
                    name="approvalMode"
                    render={({ field: { onChange, value } }) => (
                      <div
                        className="d-flex align-items-center p-1 mb-4"
                        style={{
                          backgroundColor: '#e3e5e8',
                          borderRadius: '32px',
                          width: '200px',
                          height: '40px',
                        }}
                      >
                        {/* Full Process */}
                        <div
                          onClick={() => onChange('fullProcess')}
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            height: '38px',
                            width: '150px',
                            borderRadius: '28px',
                            backgroundColor:
                              value === 'fullProcess' ? '#20ad5a' : 'transparent',
                            color: value === 'fullProcess' ? '#fff' : '#686c74',
                            fontSize: '12px',
                            fontWeight: value === 'fullProcess' ? '700' : '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Full Process
                        </div>

                        {/* MVR First */}
                        <div
                          onClick={() => onChange('mvrFirst')}
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            height: '38px',
                            width: '150px',
                            borderRadius: '28px',
                            backgroundColor:
                              value === 'mvrFirst' ? '#e99a24' : 'transparent',
                            color: value === 'mvrFirst' ? '#fff' : '#686c74',
                            fontSize: '12px',
                            fontWeight: value === 'mvrFirst' ? '700' : '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          MVR First
                        </div>
                      </div>
                    )}
                  />

                  {/* Description */}
                  <div>
                    {/* Full Process description */}
                    <div
                      className="d-flex align-items-start"
                      style={{
                        marginBottom: '10px',
                      }}
                    >
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          minWidth: '16px',
                          borderRadius: '50%',
                          backgroundColor: '#20ad5a',
                          marginTop: '6px',
                          marginRight: '16px',
                        }}
                      />

                      <div
                        style={{
                          fontSize: '12px',
                          lineHeight: '1.55',
                          color: '#62666e',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: '700',
                            color: '#171717',
                          }}
                        >
                          Full Process
                        </span>
                        {' — '}requires both MVR and the criminal check to clear before a
                        driver is approved.
                      </div>
                    </div>

                    {/* MVR First description */}
                    <div className="d-flex align-items-start">
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          minWidth: '16px',
                          borderRadius: '50%',
                          backgroundColor: '#e99a24',
                          marginTop: '6px',
                          marginRight: '16px',
                        }}
                      />

                      <div
                        style={{
                          fontSize: '12px',
                          lineHeight: '1.55',
                          color: '#62666e',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: '700',
                            color: '#171717',
                          }}
                        >
                          MVR First
                        </span>
                        {' — '}approves as soon as MVR clears. The criminal check keeps
                        running and can reject the driver afterward.
                      </div>
                    </div>
                  </div>
                </div> 
                : ''
              }
              <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', width: '100%' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                    <div className="text-white d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', fontSize: '14px', borderRadius: '20%', backgroundColor: '#4195f5 ' }}>
                      <i className="fa fa-user-plus"></i>
                    </div>
                    <div>
                      <div className="fw-bold" style={{ fontSize: '16px' }}>Driver Intake</div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>Controls what happens when a new driver applies to this metro</div>
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="isDriverIntakeCheck"
                    render={({ field: { onChange, value } }) => (
                      <div className="toggle-switch-metros">
                        <input
                          type="checkbox"
                          className="checkbox"
                          name="isDriverIntakeCheck"
                          id="driver-intake"
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                        <label className="label" htmlFor="driver-intake">
                          <span className="backgroundC" />
                          <span className="switch-metros" />
                        </label>
                      </div>
                    )}
                  />
                </div>
                  <div className="mt-2 p-2 rounded d-flex align-items-center gap-2" style={{ backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', fontSize: '13px', gap: '0.5rem' }}>
                    <i className="fa fa-clock"></i>
                    <span>New drivers who apply are added to the waitlist instead of being approved.</span>
                  </div>
              </div>
              </fieldset>
            </form>
          )}
          {isFetching ? (
            <div
              className="inner__scroll__view__container"
              style={{ height: '8rem' }}
            >
              <CircularProgress />
            </div>
          ) : (
            checkForUpdateDriversMetro && (
              <form style={{ height: isMenuOpen ? '20vh' : 'auto' }}>
                <Select
                  onChange={(item) => {
                    setSelectedOption(item);
                  }}
                  options={options}
                  value={selectedOption}
                  styles={metroOptionsStyles}
                  components={{
                    IndicatorSeparator: () => null
                  }}
                  closeMenuOnSelect={true}
                  onMenuClose={() => setIsMenuOpen(false)}
                  onMenuOpen={() => setIsMenuOpen(true)}
                  menuIsOpen={isMenuOpen}
                />
              </form>
            )
          )}
        </div>
        <div className="modal__footer">
          <button className="btn" onClick={() => actions.modal.closeMetro()}>
            Cancel
          </button>
          {isReadOnly ? null : !checkForUpdateDriversMetro ? (
            <div style={{ position: 'relative' }}>
              <button
                className={`btn btn_accent ${isUpdating || isCreating ? 'with-progress' : ''
                  }`}
                disabled={
                  isUpdating || isCreating
                  // (!form.formState.isDirty && !checkForUpdateDriversMetro) ||
                  // isBonusPercentageMatch === id?.bonus ||
                  // isBonusType === id?.bonusType
                }
                onClick={form.handleSubmit(onSubmit)}
              >
                OK
              </button>
              {(isCreating || isUpdating) && (
                <CircularProgress
                  size={18}
                  sx={{
                    color: '#fff',
                    position: 'absolute',
                    top: '50%',
                    left: '83%',
                    marginTop: '-8px',
                    marginLeft: '-10px'
                  }}
                />
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn_accent"
                disabled={isUpdatingDriver || metro === selectedOption?.value}
                onClick={onSubmit}
              >
                OK
              </button>
              {isUpdatingDriver && (
                <CircularProgress
                  size={18}
                  sx={{
                    color: '#fff',
                    position: 'absolute',
                    top: '50%',
                    left: '83%',
                    marginTop: '-8px',
                    marginLeft: '-10px'
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MetroModal;
